import { useEffect, useState } from 'react';
import { breakpoints, Button, Dialog, Inline, Stack } from '@etg/wings';
import { Pictogram } from '@eti/components';
import { useSiteContext, useTranslation } from '@eti/providers';
import { useHistory, useLocation } from 'react-router-dom';
import { useToggle } from '../../../common/custom-hooks/toggle';
import { routes } from '../../../constants/routesAndApisConstants';
import { usePaymentErrorDataLayerEffect } from '../../../common/data-layer/ecommerce/custom-hooks/usePaymentErrorDataLayerEffect';
import { errorMessageToTextKeyId } from '../constants/errorMessages';
import { makeOrderResponseMessages } from '../constants/makeOrderResponse';
import { dialogStates } from '../constants/paymentDialog';

type PaymentErrorTypes = keyof typeof makeOrderResponseMessages;

const { GENERIC_ERROR, PRECONDITIONS_VALIDATION_FAILED } = makeOrderResponseMessages;

const getTexts = (id: PaymentErrorTypes) => ({
  title: `PaymentPage.ErrorModal.${errorMessageToTextKeyId[id]}.Title`,
  message: `PaymentPage.ErrorModal.${errorMessageToTextKeyId[id]}.Message`,
  buttonLabel: `PaymentPage.ErrorModal.TryAgain.Button.Text`,
});

const UrlParamErrorDialog = () => {
  const { brand } = useSiteContext();
  const { search } = useLocation();
  const { t } = useTranslation();
  const history = useHistory();
  const [errorId, setErrorId] = useState<PaymentErrorTypes | null>(null);
  const { isOpen, handleClose, handleOpen } = useToggle(false);

  useEffect(() => {
    const errorMessage = new URLSearchParams(search).get('errorMessage') as PaymentErrorTypes;
    if (
      errorMessage &&
      Object.prototype.hasOwnProperty.call(makeOrderResponseMessages, errorMessage)
    ) {
      setErrorId(errorMessage);
      handleOpen();
    }
  }, [search, handleOpen]);

  usePaymentErrorDataLayerEffect(dialogStates.ERROR, errorId);
  const handleCloseDialog = () => {
    const searchParams = new URLSearchParams(search);
    searchParams.delete('errorMessage');

    if (errorId === PRECONDITIONS_VALIDATION_FAILED) {
      sessionStorage.setItem('hasPaymentFailed', 'true');
      history.push(routes.START);
    }
    history.replace({
      search: searchParams.toString(),
    });
    handleClose();
  };

  const { buttonLabel, message, title } = getTexts(errorId ?? GENERIC_ERROR);

  return (
    <Dialog
      closeButtonAriaLabel={t('ScreenReader.CloseButtonDialog.AriaLabel.Text')}
      data-testid="paymentError-dialog"
      isOpen={isOpen}
      title={t(title)}
    >
      <Stack>
        <Inline align="center">
          <Pictogram brand={brand.code} id="generic-error" />
        </Inline>
        <p>{t(message)}</p>
        <Inline align="end" collapseBelow={breakpoints._560}>
          <Button data-testid="dialog-close" onClick={handleCloseDialog} variant="primary">
            {t(buttonLabel)}
          </Button>
        </Inline>
      </Stack>
    </Dialog>
  );
};

export default UrlParamErrorDialog;
